import { FC, useCallback, useState } from 'react'

import {
  BulkActionsDefinition,
  FiltersDefinition,
  OnBulkActionCallback,
} from '@factorialco/f0-react/dist/experimental'
import { ExpensablesDataCollectionQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import AddToGroupModal from 'modules/expenses/components/expenses/AddToGroupModal'
import DeleteExpensableModal from 'modules/expenses/components/expenses/DeleteExpensableModal'
import RemoveFromGroupModal from 'modules/expenses/components/expenses/RemoveFromGroupModal'
import { useExpensableActions } from 'modules/expenses/hooks/useExpensableActions'

type Expensable =
  ExpensablesDataCollectionQuery['expenses']['expensablesConnection']['nodes'][number]

interface UseMyExpensesBulkActionsProps {
  expensableGroupsEnabled: boolean
  employeeId: string
}

type ModalState =
  | { type: 'closed' }
  | { type: 'add-to-group'; expensables: Expensable[] }
  | { type: 'remove-from-group'; expensables: Expensable[] }
  | { type: 'delete'; expensables: Expensable[] }

export const useMyExpensesBulkActions = ({
  expensableGroupsEnabled,
  employeeId,
}: UseMyExpensesBulkActionsProps) => {
  const [modalState, setModalState] = useState<ModalState>({ type: 'closed' })
  const { onAddToGroup, onRemoveFromGroup, onBulkDelete } = useExpensableActions()

  const closeModal = () => setModalState({ type: 'closed' })

  const bulkActions = useCallback<BulkActionsDefinition<Expensable, FiltersDefinition<string>>>(
    ({ itemsStatus }) => {
      const selectedExpensables = itemsStatus
        .filter((item) => item.checked)
        .map((item) => item.item)

      if (selectedExpensables.length === 0) {
        return { primary: [] }
      }

      const canAddToGroup =
        expensableGroupsEnabled &&
        selectedExpensables.some(
          (exp) => exp.status === 'draft' && !exp.groupId && exp.owner?.id === employeeId
        )

      const canRemoveFromGroup =
        expensableGroupsEnabled &&
        selectedExpensables.some((exp) => exp.status === 'draft' && exp.groupId)

      const canBulkDelete = selectedExpensables.some(
        (exp) => exp.status === 'draft' && exp.owner?.id === employeeId
      )

      if (!canAddToGroup && !canRemoveFromGroup && !canBulkDelete) {
        return {
          warningMessage: i18n.t('expenses_v2.list.actions.no_actions_available'),
        }
      }

      const actions = []

      if (canAddToGroup) {
        actions.push({
          id: 'add-to-group',
          label: i18n.t('expenses_v2.buttons.add_to_group_v2'),
        })
      }

      if (canRemoveFromGroup) {
        actions.push({
          id: 'remove-from-group',
          label: i18n.t('expenses_v2.buttons.remove_from_group_v2'),
        })
      }

      if (canBulkDelete) {
        actions.push({
          id: 'delete',
          label: i18n.t('expenses_v2.buttons.delete'),
        })
      }

      return { primary: actions }
    },
    [expensableGroupsEnabled, employeeId]
  )

  const handleBulkAction = useCallback<OnBulkActionCallback<Expensable, FiltersDefinition<string>>>(
    async (actionId, { itemsStatus }) => {
      const selectedExpensables = itemsStatus
        .filter((item) => item.checked)
        .map((item) => item.item)

      if (actionId === 'add-to-group') {
        const eligibleExpensables = selectedExpensables.filter(
          (exp) => exp.status === 'draft' && !exp.groupId && exp.owner?.id === employeeId
        )

        if (eligibleExpensables.length > 0) {
          setModalState({ type: 'add-to-group', expensables: eligibleExpensables })
        }
      } else if (actionId === 'remove-from-group') {
        const eligibleExpensables = selectedExpensables.filter(
          (exp) => exp.status === 'draft' && exp.groupId
        )

        if (eligibleExpensables.length > 0) {
          setModalState({ type: 'remove-from-group', expensables: eligibleExpensables })
        }
      } else if (actionId === 'delete') {
        const eligibleExpensables = selectedExpensables.filter(
          (exp) => exp.status === 'draft' && exp.owner?.id === employeeId
        )

        if (eligibleExpensables.length > 0) {
          setModalState({ type: 'delete', expensables: eligibleExpensables })
        }
      }
    },
    [employeeId]
  )

  const handleAddToGroup = async (groupId: string) => {
    if (modalState.type !== 'add-to-group') return

    const ids = modalState.expensables.map((exp) => exp.id)
    try {
      await onAddToGroup(ids, groupId)
    } finally {
      closeModal()
    }
  }

  const handleRemoveFromGroup = async () => {
    if (modalState.type !== 'remove-from-group') return

    const ids = modalState.expensables.map((exp) => exp.id)
    try {
      await onRemoveFromGroup(ids)
    } finally {
      closeModal()
    }
  }

  const handleDelete = async () => {
    if (modalState.type !== 'delete') return

    const ids = modalState.expensables.map((exp) => exp.id)
    try {
      await onBulkDelete(ids)
    } finally {
      closeModal()
    }
  }

  const Modals: FC = () => {
    if (modalState.type === 'closed') return null

    switch (modalState.type) {
      case 'add-to-group':
        return <AddToGroupModal onSubmit={handleAddToGroup} onClose={closeModal} />

      case 'remove-from-group':
        return <RemoveFromGroupModal onSubmit={handleRemoveFromGroup} onClose={closeModal} />

      case 'delete':
        return (
          <DeleteExpensableModal
            isBulk={modalState.expensables.length > 1}
            onConfirm={handleDelete}
            onClose={closeModal}
          />
        )
    }
  }

  const handlers = {
    openAddToGroupModal: (expensables: Expensable[]) =>
      setModalState({ type: 'add-to-group', expensables }),
    openRemoveFromGroupModal: (expensables: Expensable[]) =>
      setModalState({ type: 'remove-from-group', expensables }),
    openDeleteModal: (expensables: Expensable[]) => setModalState({ type: 'delete', expensables }),
  }

  return {
    bulkActions,
    handleBulkAction,
    handlers,
    Modals,
  }
}
